import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Inbox,
    Mail,
    Phone,
    Building2,
    Trash2,
    CheckCircle2,
    MessageSquare,
    Clock,
    CalendarCheck,
    XCircle,
    RefreshCw,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { listContacts, updateContactStatus, deleteContact } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const STATUS_OPTIONS = [
    { code: "new", label: "Nueva", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
    { code: "contacted", label: "Contactada", icon: MessageSquare, cls: "bg-sky-50 text-sky-700 border-sky-200" },
    { code: "demo_scheduled", label: "Demo agendada", icon: CalendarCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { code: "dismissed", label: "Descartada", icon: XCircle, cls: "bg-ink-50 text-ink-500 border-ink-200" },
];

export default function Contacts() {
    const [contacts, setContacts] = useState([]);
    const [counts, setCounts] = useState({ new: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const d = await listContacts();
            setContacts(d.requests || []);
            setCounts(d.counts || { new: 0, total: 0 });
        } catch {
            toast.error("No se pudo cargar");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const filtered = useMemo(() => {
        if (filter === "all") return contacts;
        return contacts.filter((c) => c.status === filter);
    }, [contacts, filter]);

    const onChangeStatus = async (c, newStatus) => {
        try {
            await updateContactStatus(c.id, newStatus);
            setContacts((prev) =>
                prev.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x))
            );
            if (c.status === "new" && newStatus !== "new") {
                setCounts((p) => ({ ...p, new: Math.max(0, p.new - 1) }));
            }
            toast.success("Estado actualizado");
        } catch {
            toast.error("No se pudo actualizar");
        }
    };

    const onDelete = async (c) => {
        if (!window.confirm(`¿Eliminar la solicitud de ${c.name}?`)) return;
        try {
            await deleteContact(c.id);
            setContacts((prev) => prev.filter((x) => x.id !== c.id));
            toast.success("Eliminada");
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    return (
        <Layout>
            <div className="max-w-[1200px] mx-auto p-6 md:p-8" data-testid="contacts-page">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 mb-2">
                            — Bandeja de entrada
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Solicitudes de demo
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium">
                            Leads recibidos desde el formulario de la landing pública.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={reload}
                        disabled={loading}
                        className="rounded-full border-ink-200 h-10 px-4 font-bold gap-2"
                        data-testid="contacts-refresh"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                        Refrescar
                    </Button>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {[
                        { label: "Nuevas", value: counts.new, color: "from-amber-500 to-orange-500" },
                        { label: "Total", value: counts.total, color: "from-brand-600 to-cyan-500" },
                        {
                            label: "Contactadas",
                            value: contacts.filter((c) => c.status === "contacted").length,
                            color: "from-sky-500 to-blue-500",
                        },
                        {
                            label: "Demos agendadas",
                            value: contacts.filter((c) => c.status === "demo_scheduled").length,
                            color: "from-emerald-500 to-teal-500",
                        },
                    ].map((k) => (
                        <div
                            key={k.label}
                            className="bg-white rounded-2xl p-4 border border-ink-100 shadow-pop"
                            data-testid={`contacts-kpi-${k.label}`}
                        >
                            <div
                                className={`w-9 h-9 rounded-xl bg-gradient-to-br ${k.color} text-white flex items-center justify-center shadow-pop mb-3`}
                            >
                                <Inbox size={15} />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-1">
                                {k.label}
                            </div>
                            <div className="font-display text-2xl font-black text-ink-950">
                                {k.value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filter chips */}
                <div className="flex gap-1 bg-white border border-ink-100 p-1 rounded-xl mb-5 inline-flex overflow-x-auto">
                    {[{ code: "all", label: "Todas" }, ...STATUS_OPTIONS].map((s) => (
                        <button
                            key={s.code}
                            onClick={() => setFilter(s.code)}
                            className={`px-4 h-9 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                                filter === s.code
                                    ? "bg-brand-600 text-white shadow-pop"
                                    : "text-ink-500 hover:bg-brand-50 hover:text-brand-600"
                            }`}
                            data-testid={`contacts-filter-${s.code}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                {/* List */}
                {loading ? (
                    <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center text-ink-400">
                        Cargando…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-ink-100 p-12 text-center shadow-pop">
                        <Inbox className="mx-auto text-ink-300 mb-3" size={36} strokeWidth={1.5} />
                        <div className="font-display text-xl font-black mb-1">Sin solicitudes</div>
                        <div className="text-sm text-ink-500 font-medium">
                            Cuando alguien rellene el formulario de la landing aparecerá aquí.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((c) => {
                            const st = STATUS_OPTIONS.find((s) => s.code === c.status) || STATUS_OPTIONS[0];
                            return (
                                <div
                                    key={c.id}
                                    className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop hover:shadow-pop-lg transition-all"
                                    data-testid={`contact-${c.id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${st.cls}`}
                                                >
                                                    <st.icon size={11} />
                                                    {st.label}
                                                </span>
                                                <span className="text-[10px] font-mono font-bold text-ink-400">
                                                    {fmtDate(c.created_at)}
                                                </span>
                                            </div>
                                            <div className="font-display text-lg font-bold text-ink-950 mb-1">
                                                {c.name}
                                                {c.company && (
                                                    <span className="ml-2 text-sm text-ink-500 font-medium">
                                                        · {c.company}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 text-sm text-ink-500 font-medium mb-2">
                                                <a
                                                    href={`mailto:${c.email}`}
                                                    className="inline-flex items-center gap-1.5 text-brand-600 hover:underline"
                                                    data-testid={`contact-email-${c.id}`}
                                                >
                                                    <Mail size={13} />
                                                    {c.email}
                                                </a>
                                                {c.phone && (
                                                    <a
                                                        href={`tel:${c.phone}`}
                                                        className="inline-flex items-center gap-1.5 text-brand-600 hover:underline"
                                                    >
                                                        <Phone size={13} />
                                                        {c.phone}
                                                    </a>
                                                )}
                                            </div>
                                            {c.message && (
                                                <div className="bg-ink-50 rounded-xl px-4 py-3 text-sm text-ink-700 leading-relaxed">
                                                    {c.message}
                                                </div>
                                            )}
                                        </div>
                                        <div className="shrink-0 flex flex-col gap-1.5">
                                            <select
                                                value={c.status}
                                                onChange={(e) => onChangeStatus(c, e.target.value)}
                                                className="rounded-xl border border-ink-200 h-9 px-3 text-sm font-bold bg-white"
                                                data-testid={`contact-status-${c.id}`}
                                            >
                                                {STATUS_OPTIONS.map((s) => (
                                                    <option key={s.code} value={s.code}>
                                                        {s.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <Button
                                                variant="outline"
                                                onClick={() => onDelete(c)}
                                                className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 h-9"
                                                data-testid={`contact-delete-${c.id}`}
                                            >
                                                <Trash2 size={13} className="mr-1.5" />
                                                Eliminar
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
